import React, { useEffect, useState, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { UserContext } from '../../contexts/UserContext';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';

import './JournalEntryModal.css';

const JournalEntryModal = ({
  open,
  onClose,
  onSuccess,
  journal,
  refreshVisitDetails,
}) => {
  const { user } = useContext(UserContext);
  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const getApiAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  useEffect(() => {
    if (!open || !journal) return;
    setFormData(journal.formData);
    setError(null);
  }, [open, journal]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const postCardStatus = async (token, uniqueId, status) => {
    if (!uniqueId || !status) return;
  
    const url = new URL(`${getApiBaseUrl()}/card-status`);
    url.searchParams.set('unique_id', uniqueId);
    url.searchParams.set('status', status);
  
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  
    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to update card status');
    }
  };  

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
  
    try {
      const token = await getApiAccessToken();
      if (!token) throw new Error('Unable to acquire API access token');
  
      if (
        !formData.bp_hospital ||
        !formData.instinct_visit_id ||
        !formData.patient_id ||
        !formData.unique_id
      ) {
        throw new Error('Journal is missing visit identifiers');
      }
  
      const journalRow = {
        server_id: '',
  
        bp_hospital: formData.bp_hospital,
        instinct_visit_id: formData.instinct_visit_id,
        patient_id: formData.patient_id,
        unique_id: formData.unique_id,
  
        completed_date: formData.completed_date,
        staff_id: (formData.staff_id ?? '').toUpperCase(),
        invoiceitemid: (formData.invoiceitemid ?? '').toUpperCase(),        
        charge_type: formData?.charge_type,
  
        previous_charge: Number(formData.previous_charge),
        expected_charge: Number(formData.expected_charge),
        adjusted_charge: Number(formData.adjusted_charge),
  
        label: formData.label,
        reason: formData.reason,
        note: formData.note,
  
        entry_date: formData.entry_date,
        email: 
          user?.mail ||
          user?.email ||
          user?.userPrincipalName ||
          '',
  
        includeChecked: formData.includeChecked,
        isFinalized: formData.isFinalized,
        load_date: formData.load_date,
        finalize_date: formData.finalize_date,
  
        is_escalated: formData.is_escalated,
        is_pricing: formData.is_pricing,
        is_no_error: formData.is_no_error,
        is_placeholder: formData.is_placeholder,
        is_local_leadership: formData.is_local_leadership,
      };
  
      const res = await fetch(`${getApiBaseUrl()}/journal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify([journalRow]),
      });
      
      if (!res.ok) {
        throw new Error(await res.text());
      }

      try {
        await postCardStatus(
          token,
          formData.unique_id,
          'Complete'
        );
      } catch (statusErr) {

        console.error(
          '[JournalEntryModal] Card status update failed:',
          statusErr.message
        );
      }
  
      onSuccess?.();
      await refreshVisitDetails?.({ silent: true });      
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !journal) return null;

  const { displayedFields, readonlyFields } = journal;

  const isReadOnly = field => readonlyFields.has(field);
  const show = field => displayedFields.has(field);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="journal-modal">
      <DialogTitle className="journal-modal-header">
        <span className="journal-modal-title">
          {journal.journalType
            .replace('_', ' ')
            .replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      </DialogTitle>

      <DialogContent className="journal-modal-content">
        {error && <Alert severity="error">{error}</Alert>}

        <div className="journal-section">
          <div className="journal-grid">

            {show('label') && (
              <TextField
                label="Label"
                size="small"
                className="journal-field"
                value={formData.label || ''}
                InputProps={{ readOnly: isReadOnly('label') }}
              />
            )}

            {show('expected_charge') && (
              <TextField
                label="Price"
                size="small"
                className="journal-field"
                type="number"
                value={formData.expected_charge || ''}
                onChange={
                  isReadOnly('expected_charge')
                    ? undefined
                    : (e) => handleChange('expected_charge', e.target.value)
                }
                InputProps={{ readOnly: isReadOnly('expected_charge') }}
              />
            )}

            {show('quantity') && (
              <TextField
                label="Qty"
                size="small"
                className="journal-field"
                value={formData.quantity || ''}
                InputProps={{ readOnly: isReadOnly('quantity') }}
              />
            )}

            {show('completed_date') && (
              <TextField
                label="Completed"
                size="small"
                className="journal-field"
                value={formData.completed_date || ''}
                InputProps={{ readOnly: isReadOnly('completed_date') }}
              />
            )}

            {show('invoiceitemid') && (
              <TextField
                label="PIMS ID"
                size="small"
                className="journal-field"
                value={formData.invoiceitemid || ''}
                onChange={
                  isReadOnly('invoiceitemid')
                    ? undefined
                    : (e) => handleChange('invoiceitemid', e.target.value)
                }
                InputProps={{ readOnly: isReadOnly('invoiceitemid') }}
              />
            )}

            {show('staff_id') && (
              <TextField
                label="Staff ID"
                size="small"
                className="journal-field"     
                value={formData.staff_id || ''}
                onChange={e => handleChange('staff_id', e.target.value)}
                InputProps={{ readOnly: isReadOnly('staff_id') }}
              />
            )}

          </div>
        </div>

        <div className="journal-section">
          <div className="journal-grid">

            {show('adjusted_charge') && (
              <TextField
                label="Recovered Amount"
                size="small"
                className="journal-field"
                type="number"
                value={formData.adjusted_charge || ''}
                onChange={e => handleChange('adjusted_charge', e.target.value)}
              />
            )}

            {show('reason') && (        
              <FormControl size="small" className="journal-field">
                <InputLabel id="reason-label">Reason</InputLabel>
                <Select
                  labelId="reason-label"
                  label="Reason"
                  value={formData.reason || ''}
                  onChange={e => handleChange('reason', e.target.value)}
                >
                  {['Caught in Realtime',
                    'Changed by Doctor',
                    "Code Doesn't Exist",
                    'Estimate Issue',
                    'Hospital Nuance',
                    'Overcharge',
                    'PVL Mismatch',
                    'Workflow Error',
                    'Other',
                  ].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>

          {show('note') && (
            <div className="journal-notes-wrapper">
              <TextField
                label="Notes"
                size="small"
                className="journal-field"
                multiline
                rows={4}
                fullWidth
                value={formData.note || ''}
                onChange={e => handleChange('note', e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="journal-toggles">
          {
            [
              { key: 'is_escalated', label: 'ESCALATE' },
              { key: 'is_pricing', label: 'PRICE ISSUE' },
              { key: 'is_placeholder', label: 'PLACEHOLDER' },
              { key: 'is_no_error', label: 'NO ERROR' },
              { key: 'is_local_leadership', label: 'LOCAL LEADERSHIP' },
            ]
            .map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`journal-toggle ${formData[key] ? 'active' : ''}`}
              onClick={() =>
                setFormData(prev => ({ ...prev, [key]: !prev[key] }))
              }
            >
              {label}
            </button>
          ))}
        </div>
      </DialogContent>

      <DialogActions className="journal-modal-footer">
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress
                size={16}
                thickness={5}
                sx={{ color: '#fff' }}
              />
            ) : null
          }
          sx={{
            position: 'relative',
            paddingLeft: submitting ? '18px' : undefined,
            opacity: submitting ? 0.85 : 1,
          }}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalEntryModal;